import React, { useCallback, useEffect, useState } from 'react';
import { GridContainer, Grid } from 'src/shared/grid';
import { Formik, Form } from 'formik';
import Select from '@mui/material/Select';
import { MenuItem, TextField } from '@mui/material';
import Button from '@mui/material/Button';
import 'src/features/field-sample//FieldSample.scss';
import * as Yup from 'yup';
import { useAddAlert } from 'src/store/actions';
import { useStoreContext } from 'src/store/context';
import { alertMessages, fieldsForLiveCatalog } from 'src/constants';
import { errorResponse, regex2decimals, validateInt, ValidCharacters } from 'src/store/common';
import { createUpdateLiveCatalogCall } from 'src/apis/mutationCatalogs';
import { getLiveCatalogCall } from 'src/apis/getCatalogCalls';
import LoadingSpinner from 'src/shared/loading/loading';


export const validationSchema = Yup.object().shape({
  liveCatalogName: Yup.string().nullable()
    .required('Live Fuel Catalog Name is Required'),
  /*HERB VALIDATION*/
  herb_temp_min_max: Yup.number().nullable().min(-20, "Herbaceous Temp Min Max must be greater than or equal to -20")
    .max(20, "Herbaceous Temp Min Max must be less than or equal to 20")
    .required('Herbaceous Temp Min Max is Required')
    .test("greater-or-equal-max", "Herbaceous Temp Min Max must be greater than or equal to Herbaceous Temp Min Min", function(value) {
      const min = this.parent.herb_temp_min_min;
      if(value == null || min == null || min > 20) return true;
      return value>=min;
    }),
  herb_temp_min_min: Yup.number().nullable().min(-20, "Herbaceous Temp Min Min must be greater than or equal to -20")
    .max(20, "Herbaceous Temp Min Min must be less than or equal to 20")
    .required('Herbaceous Temp Min Min is Required')
    .test("less-or-equal-max", "Herbaceous Temp Min Min must be less than or equal to Herbaceous Temp Min Max", function(value) {
      const max = this.parent.herb_temp_min_max;
      if(value == null || max == null || max < -20) return true;
      return value<=max;
    }),
  herb_vpd_min: Yup.number().nullable().min(0, "Herbaceous VPD Min must be greater than or equal to 0")
    .max(10000, "Herbaceous VPD Min must be less than or equal to 10000")
    .required('Herbaceous VPD Min is Required')
    .test("less-or-equal-max", "Herbaceous VPD Min must be less than or equal to Herbaceous VPD Max", function(value) {
      const max = this.parent.herb_vpd_max;
      if(value == null || max == null || max < 0) return true;
      return value<=max;
    }),
  herb_vpd_max: Yup.number().nullable().min(0, "Herbaceous VPD Max must be greater than or equal to 0")
    .max(10000, "Herbaceous VPD Max must be less than or equal to 10000")
    .required('Herbaceous VPD Max is Required')
    .test("greater-or-equal-max", "Herbaceous VPD Max must be greater than or equal to Herbaceous VPD Min", function(value) {
      const min = this.parent.herb_vpd_min;
      if(value == null || min == null || min > 10000) return true;
      return value>=min;
    }),
  herb_day_length_min: Yup.number().nullable().min(0, "Herbaceous Day Length Min must be greater than or equal to 0")
    .max(86400, "Herbaceous Day Length Min must be less than or equal to 86400")
    .required('Herbaceous Day Length Min is Required')
    .test("less-or-equal-max", "Herbaceous Day Length Min must be less than or equal to Herbaceous Day Length Max", function(value) {
      const max = this.parent.herb_day_length_max;
      if(value == null || max == null || max < 0) return true;
      return value<=max;
    }),
  herb_day_length_max: Yup.number().nullable().min(0, "Herbaceous Day Length Max must be greater than or equal to 0")
    .max(86400, "Herbaceous Day Length Max must be less than or equal to 86400")
    .required('Herbaceous Day Length Max is Required')
    .test("greater-or-equal-max", "Herbaceous Day Length Max must be greater than or equal to Herbaceous Day Length Min", function(value) {
      const min = this.parent.herb_day_length_min;
      if(value == null || min == null || min > 86400) return true;
      return value>=min;
    }),
  herb_vpd_avg_flg: Yup.string().nullable()
    .required('Herbaceous VPD Avg Flag is Required'),
  herb_days_avg: Yup.number().nullable().min(0, "Herbaceous Days Avg must be greater than or equal to 0")
    .max(100, "Herbaceous Days Avg must be less than or equal to 100")
    .required('Herbaceous Days Avg is Required'),
  herb_max_gsi: Yup.number().nullable().min(0, "Herbaceous Max GSI must be greater than or equal to 0")
    .max(1, "Herbaceous Max GSI must be less than or equal to 1")
    .required('Herbaceous Max GSI is Required'),    
  herb_greenup: Yup.number().nullable().min(0.11, "Herbaceous Greenup Threshold must be greater than or equal to 0.11")
    .max(0.99, "Herbaceous Greenup Threshold must be less than or equal to 0.99")
    .required('Herbaceous Greenup Threshold is Required'),
  herb_max: Yup.number().nullable().min(0, "Herbaceous FM Max must be greater than or equal to 0")
    .max(500, "Herbaceous FM Max must be less than or equal to 500")
    .required('Herbaceous FM Max is Required')
    .test("greater-or-equal-max", "Herbaceous FM Max must be greater than or equal to Herbaceous FM Min", function(value) {
      const min = this.parent.herb_min;
      if(value == null || min == null || min > 500) return true;
      return value>=min;
    }),
  herb_min: Yup.number().nullable().min(0, "Herbaceous FM Min must be greater than or equal to 0")
    .max(500, "Herbaceous FM Min must be less than or equal to 500")
    .required('Herbaceous FM Min is Required')
    .test("less-or-equal-max", "Herbaceous FM Min must be less than or equal to Herbaceous FM Max", function(value) {
      const max = this.parent.herb_max;
      if(value == null || max == null || max < 0) return true;
      return value<=max;
    }),
  herb_precip_total_min: Yup.number().nullable().min(0, "Herbaceous Running Precip Total Min must be greater than or equal to 0")
    .max(50, "Herbaceous Running Precip Total Min must be less than or equal to 50")
    .required('Herbaceous Running Precip Total Min is Required')
    .test("less-or-equal-max", "Herbaceous Running Precip Total Min must be less than or equal to Herbaceous Running Precip Total Max", function(value) {
      const max = this.parent.herb_precip_total_max;
      if(value == null || max == null || max < 0) return true;
      return value<=max;
    }),
  herb_precip_total_max: Yup.number().nullable().min(0, "Herbaceous Running Precip Total Max must be greater than or equal to 0")
    .max(50, "Herbaceous Running Precip Total Max must be less than or equal to 50")
    .required('Herbaceous Running Precip Total Max is Required')
    .test("greater-or-equal-max", "Herbaceous Running Precip Total Max must be greater than or equal to Herbaceous Running Precip Total Min", function(value) {
      const min = this.parent.herb_precip_total_min;
      if(value == null || min == null || min > 50) return true;
      return value>=min;
    }),
  herb_total_precip_days: Yup.number().nullable().min(0, "Herbaceous Running Total Precip Days must be greater than or equal to 0")
    .max(120, "Herbaceous Running Total Precip Days must be less than or equal to 120")
    .required('Herbaceous Running Total Precip Days is Required'),
  herb_use_precip_flg: Yup.string().nullable()
    .required('Herbaceous Use Precip Flag is Required'),
  herb_calibration: Yup.string().nullable()
    .required('Herbaceous Calibration is Required'),
  /*WOODY VALIDATION*/
  woody_temp_min_max: Yup.number().nullable().min(-20, "Woody Temp Min Max must be greater than or equal to -20")
    .max(20, "Woody Temp Min Max must be less than or equal to 20")
    .required('Woody Temp Min Max is Required')
    .test("greater-or-equal-max", "Woody Temp Min Max must be greater than or equal to Woody Temp Min Min", function(value) {
      const min = this.parent.woody_temp_min_min;
      if(value == null || min == null || min > 20) return true;
      return value>=min;
    }),
  woody_temp_min_min: Yup.number().nullable().min(-20, "Woody Temp Min Min must be greater than or equal to -20")
    .max(20, "Woody Temp Min Min must be less than or equal to 20")
    .required('Woody Temp Min Min is Required')
    .test("less-or-equal-max", "Woody Temp Min Min must be less than or equal to Woody Temp Min Max", function(value) {
      const max = this.parent.woody_temp_min_max;
      if(value == null || max == null || max < -20) return true;
      return value<=max;
    }),
  woody_vpd_min: Yup.number().nullable().min(0, "Woody VPD Min must be greater than or equal to 0")
    .max(10000, "Woody VPD Min must be less than or equal to 10000")
    .required('Woody VPD Min is Required')
    .test("less-or-equal-max", "Woody VPD Min must be less than or equal to Woody VPD Max", function(value) {
      const max = this.parent.woody_vpd_max;
      if(value == null || max == null || max < 0) return true;
      return value<=max;
    }),
  woody_vpd_max: Yup.number().nullable().min(0, "Woody VPD Max must be greater than or equal to 0")
    .max(10000, "Woody VPD Max must be less than or equal to 10000")
    .required('Woody VPD Max is Required')
    .test("greater-or-equal-max", "Woody VPD Max must be greater than or equal to Woody VPD Min", function(value) {
      const min = this.parent.woody_vpd_min;
      if(value == null || min == null || min > 10000) return true;
      return value>=min;
    }),
  woody_day_length_min: Yup.number().nullable().min(0, "Woody Day Length Min must be greater than or equal to 0")
    .max(86400, "Woody Day Length Min must be less than or equal to 86400")
    .required('Woody Day Length Min is Required')
    .test("less-or-equal-max", "Woody Day Length Min must be less than or equal to Woody Day Length Max", function(value) {
      const max = this.parent.woody_day_length_max;
      if(value == null || max == null || max < 0) return true;
      return value<=max;
    }),
  woody_day_length_max: Yup.number().nullable().min(0, "Woody Day Length Max must be greater than or equal to 0")
    .max(86400, "Woody Day Length Max must be less than or equal to 86400")
    .required('Woody Day Length Max is Required')
    .test("greater-or-equal-max", "Woody Day Length Max must be greater than or equal to Woody Day Length Min", function(value) {
      const min = this.parent.woody_day_length_min;
      if(value == null || min == null || min > 86400) return true;
      return value>=min;
    }),
  woody_vpd_avg_flg: Yup.string().nullable()
    .required('Woody VPD Avg Flag is Required'),
  woody_days_avg: Yup.number().nullable().min(0, "Woody Days Avg must be greater than or equal to 0")
    .max(100, "Woody Days Avg must be less than or equal to 100")
    .required('Woody Days Avg is Required'),
  woody_max_gsi: Yup.number().nullable().min(0, "Woody Max GSI must be greater than or equal to 0")
    .max(1, "Woody Max GSI must be less than or equal to 1")
    .required('Woody Max GSI is Required'),    
  woody_greenup: Yup.number().nullable().min(0.11, "Woody Greenup Threshold must be greater than or equal to 0.11")
    .max(0.99, "Woody Greenup Threshold must be less than or equal to 0.99")
    .required('Woody Greenup Threshold is Required'),
  woody_max: Yup.number().nullable().min(0, "Woody FM Max must be greater than or equal to 0")
    .max(500, "Woody FM Max must be less than or equal to 500")
    .required('Woody FM Max is Required')
    .test("greater-or-equal-max", "Woody FM Max must be greater than or equal to Woody FM Min", function(value) {
      const min = this.parent.woody_min;
      if(value == null || min == null || min > 500) return true;
      return value>=min;
    }),
  woody_min: Yup.number().nullable().min(0, "Woody FM Min must be greater than or equal to 0")
    .max(500, "Woody FM Min must be less than or equal to 500")
    .required('Woody FM Min is Required')
    .test("less-or-equal-max", "Woody FM Min must be less than or equal to Woody FM Max", function(value) {
      const max = this.parent.woody_max;
      if(value == null || max == null || max < 0) return true;
      return value<=max;
    }),
  woody_precip_total_min: Yup.number().nullable().min(0, "Woody Running Precip Total Min must be greater than or equal to 0")
    .max(50, "Woody Running Precip Total Min must be less than or equal to 50")
    .required('Woody Running Precip Total Min is Required')
    .test("less-or-equal-max", "Woody Running Precip Total Min must be less than or equal to Woody Running Precip Total Max", function(value) {
      const max = this.parent.woody_precip_total_max;
      if(value == null || max == null || max < 0) return true;
      return value<=max;
    }),
  woody_precip_total_max: Yup.number().nullable().min(0, "Woody Running Precip Total Max must be greater than or equal to 0")
    .max(50, "Woody Running Precip Total Max must be less than or equal to 50")
    .required('Woody Running Precip Total Max is Required')
    .test("greater-or-equal-max", "Woody Running Precip Total Max must be greater than or equal to Woody Running Precip Total Min", function(value) {
      const min = this.parent.woody_precip_total_min;
      if(value == null || min == null || min > 50) return true;
      return value>=min;
    }),
  woody_total_precip_days: Yup.number().nullable().min(0, "Woody Running Total Precip Days must be greater than or equal to 0")
    .max(120, "Woody Running Total Precip Days must be less than or equal to 120")
    .required('Woody Running Total Precip Days is Required'),
  woody_use_precip_flg: Yup.string().nullable()
    .required('Woody Use Precip Flag is Required'),
  woody_calibration: Yup.string().nullable()
    .required('Woody Calibration is Required'),
});

type ReportProps ={
  setOpen: any,
  setCreateUpdateLiveSuccess: any,
  editLiveFuelCatalog?: any,
}
interface FormVals {
  liveCatalogId: number|null,
  liveCatalogName: string,
  herb_temp_min_max: number|null,
  herb_temp_min_min: number|null,
  herb_vpd_min: number|null,
  herb_vpd_max: number|null,
  herb_max_gsi: number|null,
  herb_greenup: number|null,
  herb_max: number|null,
  herb_min: number|null,
  herb_days_avg: number|null,
  herb_day_length_min: number|null,
  herb_day_length_max: number|null,
  herb_vpd_avg_flg: string,
  herb_precip_total_min: number|null,
  herb_precip_total_max: number|null,
  herb_total_precip_days: number|null,
  herb_use_precip_flg: string,
  herb_calibration: string,
  woody_days_avg: number|null,
  woody_max_gsi: number|null,
  woody_greenup: number|null,
  woody_max: number|null,
  woody_min: number|null,
  woody_temp_min_max: number|null,
  woody_temp_min_min: number|null,
  woody_vpd_min: number|null,
  woody_vpd_max: number|null,
  woody_day_length_min: number|null,
  woody_day_length_max: number|null,
  woody_vpd_avg_flg: string,
  woody_precip_total_min: number|null,
  woody_precip_total_max: number|null,
  woody_total_precip_days: number|null,
  woody_use_precip_flg: string,
  woody_calibration: string,
}
export const AddLiveCatalog: React.FC<ReportProps> = ({setOpen, setCreateUpdateLiveSuccess, editLiveFuelCatalog}) => {

  const [, dispatch] = useStoreContext();
  const addAlert = useAddAlert(dispatch);
  const [loading,setLoading] = useState<boolean>(false)
  const [initVals, setInitVals] = useState<FormVals>({
    liveCatalogId: null,
    liveCatalogName: '',
    herb_temp_min_max: null,
    herb_temp_min_min: null,
    herb_vpd_min: 0,
    herb_vpd_max: 1,
    herb_max_gsi: null,
    herb_greenup: null,
    herb_max: null,
    herb_min: null,
    herb_days_avg: null,
    herb_day_length_min: null,
    herb_day_length_max: null,
    herb_vpd_avg_flg: 'N',
    herb_precip_total_min: null,
    herb_precip_total_max: null,
    herb_total_precip_days: null,
    herb_use_precip_flg: '',
    herb_calibration: '0',

    woody_days_avg: null,
    woody_max_gsi: null,
    woody_greenup: null,
    woody_max: null,
    woody_min: null,
    woody_temp_min_max: null,
    woody_temp_min_min: null,
    woody_vpd_min: 0,
    woody_vpd_max: 1,
    woody_day_length_min: null,
    woody_day_length_max: null,
    woody_vpd_avg_flg: 'N',
    woody_precip_total_min: null,
    woody_precip_total_max: null,
    woody_total_precip_days: null,
    woody_use_precip_flg: '',
    woody_calibration: '0',
  })

  const handleSubmit= (values:any)=>{
    setLoading(true);
    (async()=>{
      try{
        const paramsToSend = {
          id: values.liveCatalogId === null ? null : Number(values.liveCatalogId),
          name: values.liveCatalogName,
          herbDaysAvg: Number(values.herb_days_avg),
          herbMaxGsi: Number(values.herb_max_gsi),
          herbGreenup: Number(values.herb_greenup),
          herbMax: Number(values.herb_max),
          herbMin: Number(values.herb_min),
          herbTempMinMax: Number(values.herb_temp_min_max),
          herbTempMinMin: Number(values.herb_temp_min_min),
          herbVpdMin: Number(values.herb_vpd_min),
          herbVpdMax: Number(values.herb_vpd_max),
          herbDayLengthMin: Number(values.herb_day_length_min),
          herbDayLengthMax: Number(values.herb_day_length_max),
          herbVpdAvgFlg: values.herb_vpd_avg_flg,
          herbPrecipTotalMin: Number(values.herb_precip_total_min),
          herbPrecipTotalMax: Number(values.herb_precip_total_max),
          herbTotalPrecipDays: Number(values.herb_total_precip_days),
          herbUsePrecipFlg: values.herb_use_precip_flg,
          herbCalibration: values.herb_calibration,

          woodyDaysAvg: Number(values.woody_days_avg),
          woodyMaxGsi: Number(values.woody_max_gsi),
          woodyGreenup: Number(values.woody_greenup),
          woodyMax: Number(values.woody_max),
          woodyMin: Number(values.woody_min),
          woodyTempMinMax: Number(values.woody_temp_min_max),
          woodyTempMinMin: Number(values.woody_temp_min_min),
          woodyVpdMin: Number(values.woody_vpd_min),
          woodyVpdMax: Number(values.woody_vpd_max),
          woodyDayLengthMin: Number(values.woody_day_length_min),
          woodyDayLengthMax: Number(values.woody_day_length_max),
          woodyVpdAvgFlg: values.woody_vpd_avg_flg,
          woodyPrecipTotalMin: Number(values.woody_precip_total_min),
          woodyPrecipTotalMax: Number(values.woody_precip_total_max),
          woodyTotalPrecipDays: Number(values.woody_total_precip_days),
          woodyUsePrecipFlg: values.woody_use_precip_flg,
          woodyCalibration: values.woody_calibration
        }
        const response:any = await createUpdateLiveCatalogCall(paramsToSend, addAlert);
        if(response?.data.data.createUpdateLiveFuelCatalog.data.name?.length>0){
          setCreateUpdateLiveSuccess(true)
          //save banner
          if(values.liveCatalogId===null){
            errorResponse('', addAlert, `The Live Fuel Catalog, ${response.data.data.createUpdateLiveFuelCatalog.data.name}, was saved.`, true);
          }
          else{
            errorResponse('', addAlert, `The changes to the Live Fuel Catalog, ${response.data.data.createUpdateLiveFuelCatalog.data.name}, was saved.`, true);
          }
          
          setOpen(false)
        }
        setLoading(false)

      }
      catch(error:any){
        setLoading(false)

        if(error?.message?.toLowerCase().includes('unique')){
          addAlert({
            id: '',
            message: alertMessages.uniqueCatalogName,
            type: 'error-info',
            icon: 'fa-exclamation-triangle'
          });
        }
        else{
          addAlert({
            id: '',
            message: alertMessages.liveCatalogCreate,
            type: 'error-info',
            icon: 'fa-exclamation-triangle'
          });
        }
      }
    })();
  }

  /** Make the api call for the Live Fuel Catalog Detail */
  const liveCatalogCallback = useCallback((request: any) => {
    (async () => {
      try {
        const liveCatalogResponse: any = await getLiveCatalogCall(request, addAlert);
        if(liveCatalogResponse.data.length > 0) {
          let editValues = {
            liveCatalogId: liveCatalogResponse.data[0].gsiParameterCatalogId,
            liveCatalogName: liveCatalogResponse.data[0].catalogName,
            herb_temp_min_max: liveCatalogResponse.data[0].herbTempMinMax,
            herb_temp_min_min: liveCatalogResponse.data[0].herbTempMinMin,
            herb_vpd_min: liveCatalogResponse.data[0].herbVpdMin,
            herb_vpd_max: liveCatalogResponse.data[0].herbVpdMax,
            herb_max_gsi: liveCatalogResponse.data[0].herbMaxGsi,
            herb_greenup: liveCatalogResponse.data[0].herbGreenup,
            herb_max: liveCatalogResponse.data[0].herbMax,
            herb_min: liveCatalogResponse.data[0].herbMin,
            herb_days_avg: liveCatalogResponse.data[0].herbDaysAvg,
            herb_day_length_min: liveCatalogResponse.data[0].herbDayLengthMin,
            herb_day_length_max: liveCatalogResponse.data[0].herbDayLengthMax,
            herb_vpd_avg_flg: liveCatalogResponse.data[0].herbVpdAvgFlg,
            herb_precip_total_min: liveCatalogResponse.data[0].herbPrecipTotalMin,
            herb_precip_total_max: liveCatalogResponse.data[0].herbPrecipTotalMax,
            herb_total_precip_days: liveCatalogResponse.data[0].herbTotalPrecipDays,
            herb_use_precip_flg: liveCatalogResponse.data[0].herbUsePrecipFlg,
            herb_calibration: liveCatalogResponse.data[0].herbCalibration,
            woody_days_avg: liveCatalogResponse.data[0].woodyDaysAvg,
            woody_max_gsi: liveCatalogResponse.data[0].woodyMaxGsi,
            woody_greenup: liveCatalogResponse.data[0].woodyGreenup,
            woody_max: liveCatalogResponse.data[0].woodyMax,
            woody_min: liveCatalogResponse.data[0].woodyMin,
            woody_temp_min_max: liveCatalogResponse.data[0].woodyTempMinMax,
            woody_temp_min_min: liveCatalogResponse.data[0].woodyTempMinMin,
            woody_vpd_min: liveCatalogResponse.data[0].woodyVpdMin,
            woody_vpd_max: liveCatalogResponse.data[0].woodyVpdMax,
            woody_day_length_min: liveCatalogResponse.data[0].woodyDayLengthMin,
            woody_day_length_max: liveCatalogResponse.data[0].woodyDayLengthMax,
            woody_vpd_avg_flg: liveCatalogResponse.data[0].woodyVpdAvgFlg,
            woody_precip_total_min: liveCatalogResponse.data[0].woodyPrecipTotalMin,
            woody_precip_total_max: liveCatalogResponse.data[0].woodyPrecipTotalMax,
            woody_total_precip_days: liveCatalogResponse.data[0].woodyTotalPrecipDays,
            woody_use_precip_flg: liveCatalogResponse.data[0].woodyUsePrecipFlg,
            woody_calibration: liveCatalogResponse.data[0].woodyCalibration,
          }
          setInitVals(editValues);
        }
      } catch(error) {
        console.log('error', error)
        addAlert({
          id: '',
          message: alertMessages.retrieveCatalog,
          type: 'error-info',
          icon: 'fa-exclamation-triangle'
        });
      }
    })();
  }, [addAlert]);

  /* use Effect to check and make a call to get the edit area */
  useEffect(()=>{
      if(editLiveFuelCatalog){
        const request = {
          id: Number(editLiveFuelCatalog),
          sortBy: 'catalog_name',
          sortOrder: 'asc'
        }
        liveCatalogCallback(request);
      }
      else{
        let initValues={
            liveCatalogId: null,
            liveCatalogName: '',
            herb_temp_min_max: null,
            herb_temp_min_min: null,
            herb_vpd_min: 0,
            herb_vpd_max: 1,
            herb_max_gsi: null,
            herb_greenup: null,
            herb_max: null,
            herb_min: null,
            herb_days_avg: null,
            herb_day_length_min: null,
            herb_day_length_max: null,
            herb_vpd_avg_flg: 'N',
            herb_precip_total_min: null,
            herb_precip_total_max: null,
            herb_total_precip_days: null,
            herb_use_precip_flg: '',
            herb_calibration: '0',
            woody_days_avg: null,
            woody_max_gsi: null,
            woody_greenup: null,
            woody_max: null,
            woody_min: null,
            woody_temp_min_max: null,
            woody_temp_min_min: null,
            woody_vpd_min: 0,
            woody_vpd_max: 1,
            woody_day_length_min: null,
            woody_day_length_max: null,
            woody_vpd_avg_flg: 'N',
            woody_precip_total_min: null,
            woody_precip_total_max: null,
            woody_total_precip_days: null,
            woody_use_precip_flg: '',
            woody_calibration: '0',
        }
        setInitVals(initValues);
      }

  },[addAlert, editLiveFuelCatalog, liveCatalogCallback]);
  
  return(
  <GridContainer className="removePadding smooth-scrollbar" style={{height: '80%', maxHeight: '95%', minHeight:'50%', overflowX: 'hidden', paddingBottom: '2rem'}}>
      {(loading) && (
        <div>
          <LoadingSpinner />
        </div>
      )}
    <Grid row gap="md" className='page-heading'>
      <Grid
        desktop={{col:12}}
        className="display-flex flex-row flex-align-center flex-justify margin-bottom-205 paddingForModal">
          {
            editLiveFuelCatalog ? (
              <span className="font-sans-lg text-bold">Edit Live Catalog</span>
            ):(
              <span className="font-sans-lg text-bold">Add a New Live Catalog</span>
            )
          }
      </Grid>
    </Grid>
    <hr style={{width: '100%', marginBottom: '2rem', marginTop: '2rem'}}></hr>

    <Formik
      initialValues={initVals}
      validateOnBlur={true}
      validateOnChange={true}
      validateOnMount={true}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={(values) => handleSubmit(values) }>
      {({
        values,
        errors,
        touched,
        handleSubmit,
        setFieldValue,
        isValid,
        setFieldTouched
      }) => { 
          const setValue = (event: any, type: string) => {
            setFieldTouched(type);
            if (event.target.value.length === 1 && /^\s/.test(event.target.value)) {
              return
            }
            if(type==='herb_greenup' 
                || type==='woody_greenup' 
                || type==='herb_max_gsi'
                || type==='herb_precip_total_min'
                || type==='herb_precip_total_max'
                || type ==='woody_max_gsi'
                || type ==='woody_precip_total_min'
                || type ==='woody_precip_total_max'){
              if( regex2decimals.test((event.target.value).toString())){
                setFieldValue(type, event.target.value);
              }
            } else{
              setFieldValue(type, event.target.value);
            }
          }

          const setIntValue = (event: any, type: string) => {
            setFieldTouched(type);
            const inputValue = event.target.value;
            const filteredValue = inputValue.replaceAll(/(?!^-)[^0-9]/g, '');
            const intValue = filteredValue === ''  ? '' : filteredValue ==='-'? '-':Number.parseInt(filteredValue, 10);
            setFieldValue(type, intValue==='-'?null: intValue);
          }

          return (
            <Form onSubmit={handleSubmit}>
              <div className="paddingForm">
                {
                  fieldsForLiveCatalog.map((field) => {
                    if ((field.label?.includes('Use Precip Flag') || field.label?.includes('VPD Avg Flag')) && (!field.label?.includes('Herbaceous VPD M') && !field.label?.includes('Woody VPD M'))) {
                      return (
                        <Grid row desktop={{ col: 5 }} key={`${field.id}`}>
                          <label id={field.id} htmlFor={field.id}>{field.label}*
                            <Select
                              displayEmpty
                              key={`${field.id}`}
                              renderValue={values[field.name] !== "" ? undefined : () => `Select a ${field.label}`}
                              labelId={field.id}
                              onBlur={() => setFieldTouched(field.name)}
                              error={!!errors[field.name] && !!touched[field.name]}
                              name={field.id}
                              onChange={(e: any) => {setValue(e, field.name)}}
                              label={`Select a ${field.label}`}
                              value={values[field.name]}
                              className="select-styles dataDownload"
                              placeholder={field.placeHolder}
                              disabled={field.disable}
                              id={field.id}
                            >
                              <MenuItem value="Y" key="Yes">Yes</MenuItem>
                              <MenuItem value="N" key="No">No</MenuItem>
                            </Select>
                          </label>
                        </Grid>
                      )

                    }
                    else if(field.label?.includes('Calibration')){
                      return (
                        <Grid row desktop={{ col: 5 }} key={`${field.id}`}>
                          <label id={field.id} htmlFor={field.id}>{field.label}*
                            <Select
                              displayEmpty
                              key={`${field.id}`}
                              renderValue={values[field.name] !== "" ? undefined : () => `Select a ${field.label}`}
                              labelId={field.id}
                              onBlur={() => setFieldTouched(field.name)}
                              error={!!errors[field.name] && !!touched[field.name]}
                              name={field.id}
                              onChange={(e: any) => {setValue(e, field.name)}}
                              label={`Select a ${field.label}`}
                              value={values[field.name]}
                              className="select-styles dataDownload"
                              placeholder={field.placeHolder}
                              disabled={field.disable}
                              id={field.id}
                            >
                              <MenuItem value="1" key="1">On</MenuItem>
                              <MenuItem value="0" key="0">Off</MenuItem>
                            </Select>
                          </label>
                        </Grid>
                      )

                    
                    }
                    else {
                      return (
                        (!field.label?.includes('Herbaceous VPD M') && !field.label?.includes('Woody VPD M')) &&
                        <Grid row desktop={{ col: 7 }} key={`${field.id}`}>
                          <label id={field.id} htmlFor={field.id}>{field.label}*</label>
                          <TextField
                            key={`${field.id}`}
                            value={values[field.name]}
                            onBlur={() => setFieldTouched(field.name)}
                            error={!!errors[field.name] && !!touched[field.name]}
                            helperText={errors[field.name] && touched[field.name] ? errors[field.name] : ''}
                            id={field.id}
                            type={field.type}
                            size='medium'
                            fullWidth
                            onChange={(e: any) => {field.numberType==='INT'? setIntValue(e, field.name):setValue(e, field.name)}}
                            placeholder={field.placeHolder}
                            disabled={field.disable}
                            onInput={(e) => field.numberType==='INT' && validateInt(e, ValidCharacters.Numeric, '-')}

                            InputProps={{
                              inputProps: {
                                min: field.min,
                                max: field.max
                              }
                            }}
                          />
                        </Grid>
                      )
                    }
                  })
                }

              </div>

              <Grid row className='modalBottomDiv'>
                <Grid desktop={{ col: 10 }} className="modalCancelDiv">
                  <Button variant="outlined" style={{ backgroundColor: 'white' }} onClick={() => { setOpen(false) }}>
                    Cancel
                  </Button>
                </Grid>
                {
                  editLiveFuelCatalog ? (
                    <Grid desktop={{ col: 2 }} className='modalSubmitDiv'>
                      <Button variant="contained" type="submit" aria-disabled={!isValid}>
                        Save
                      </Button>
                    </Grid>
                  ) : (
                    <Grid desktop={{ col: 2 }} className='modalSubmitDiv'>
                      <Button variant="contained" type="submit" aria-disabled={!isValid}>
                        Add
                      </Button>
                    </Grid>
                  )
                }
              </Grid>
            </Form>
          )
        }}
    </Formik>
  </GridContainer>
  )
}